import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Post } from '@/types/post';
import styled from 'styled-components';
import SideNav from '@/components/Admin/SideNav';
import { colors } from '@/constants/colors';
import { canOpenWifiPanel } from '@/constants/staffTabs';
import PromptWrapper from '@/components/Admin/Templates/PromptWrapper';
import { useUIStore } from '@/store/useUIStore';
import AppToasts from '@/components/AppToasts';
import {
   fetchTransactions,
   getTotalSales,
   getTotalSalesTodayAndYesterday,
   getTotalSalesThisWeekAndLastWeek,
   getTotalSalesThisMonthAndLastMonth,
} from '@/api/transaction';
import { TransactionResponse } from '@/api/transaction';
import { fetchStores } from '@/api/store';
import Dropdown from '@/components/Admin/UI/Dropdown';
import { ISP_ENABLED } from '@/pages/Admin/Isp/ispEnabled';
import { DropdownActionType } from '@/types/shared';
import { useDistributorStatus } from '@/hooks/useDistributorStatus';
import { useResellerStatus } from '@/hooks/useResellerStatus';
import { TableStore } from './Stores';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useIsMobile';
import SaveChangesModal from '@/components/Admin/SaveChangesModal';
import useSaveChangesStore from '@/store/useSaveChangesStore';
import useAuthStore from '@/store/authStore';
import VerifyEmailBlocker from '@/components/Admin/VerifyEmailBlocker';
import useThemeStore from '@/store/useThemeStore';
import SurfaceSwitcher from '@/components/Admin/SurfaceSwitcher';
import { ShareDialog } from '@/pages/Admin/Team';
import { HelpingBanner, PendingInviteBanner, AccountStack } from '@/components/Admin/WorkspaceSwitcher';
import { useTeamStore, PENDING_INVITE_KEY } from '@/store/useTeamStore';
import NotificationBell from '@/components/Admin/NotificationBell';
import { surfaceFromUrl, useSurfaceStore } from '@/store/useSurfaceStore';
import { useAutoDetectSurface } from '@/hooks/useAutoDetectSurface';
import { CommunityButton, useMarkDashboardShell } from '@/features/community/CommunityDock';
import { useMyProfile, useProfileMenu } from '@/features/profile/ProfileModal';
type Props = {
   active: string;
   children: React.ReactNode;
};

const DashboardLayout = ({ active, children }: Props) => {
   const { outsideClickId } = useUIStore();
   const { user } = useAuthStore();
   const phone = useIsMobile(768);
   const { isDistributor } = useDistributorStatus();
   const { isReseller } = useResellerStatus();
   const { mode: themeMode, toggle: toggleTheme } = useThemeStore();
   // Share lives in the header like Canva/Figma (Ray 2026-10-14), not a sidebar page.
   const [shareOpen, setShareOpen] = useState(false);
   const navigate = useNavigate();
   const location = useLocation();
   const { surface: currentSurface, setSurface } = useSurfaceStore();
   const acting = useTeamStore((s) => s.acting);
   // Community (a drawer over every dashboard page) may connect once a dashboard page shows.
   useMarkDashboardShell();
   // Header menu → Profile (Ray 2026-10-25); the header shows my profile name and photo.
   const profileMenu = useProfileMenu();
   const { data: myProfile } = useMyProfile();

   // Team invite opened while signed out → after sign-in, finish accepting.
   useEffect(() => {
      let pending: string | null = null;
      try {
         pending = window.localStorage.getItem(PENDING_INVITE_KEY);
      } catch {
         pending = null;
      }
      if (pending) navigate(`/team/accept?token=${encodeURIComponent(pending)}`, { replace: true });
   }, [navigate]);

   useEffect(() => {
      const inferred = surfaceFromUrl(location.pathname);
      if (inferred && inferred !== currentSurface) {
         setSurface(inferred);
      }
   }, [location.pathname, currentSurface, setSurface]);

   useAutoDetectSurface();

   // console.log('DashboardLayout user:', user);

   // console.log(`user--`, user);
   // const { data: transactions, isLoading: isLoadingTransactions } =
   //    useQuery<TransactionResponse>({
   //       queryKey: ['transactions'],
   //       queryFn: fetchTransactions,
   //    });

   // const { data: stores, isLoading: isLoadingStores } = useQuery({
   //    queryKey: ['stores'],
   //    queryFn: fetchStores,
   // });

   // const { data: totalSales, isLoading: isLoadingTotalSales } = useQuery({
   //    queryKey: ['totalSales'],
   //    queryFn: getTotalSales,
   // });

   // const { data: todayYesterdaySales, isLoading: isLoadingTodayYesterday } =
   //    useQuery({
   //       queryKey: ['todayYesterdaySales'],
   //       queryFn: getTotalSalesTodayAndYesterday,
   //    });

   // const { data: weekSales, isLoading: isLoadingWeek } = useQuery({
   //    queryKey: ['weekSales'],
   //    queryFn: getTotalSalesThisWeekAndLastWeek,
   // });

   // const { data: monthSales, isLoading: isLoadingMonth } = useQuery({
   //    queryKey: ['monthSales'],
   //    queryFn: getTotalSalesThisMonthAndLastMonth,
   // });

   const {
      show,
      template,
      title,
      data: uiData,
      handleOutsideClick,
      handleShow,
      actions,
   } = useUIStore();

   const { clearAuthWithCache } = useAuth();
   const { showSave, handleShowSave, handleCloseSave, handleSave } =
      useSaveChangesStore();

   const handleClickOutside = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>
   ) => {
      const target = e.target as HTMLElement;
      handleOutsideClick(target.id);
   };

   const isAdmin = user?.role === 'admin';
   const isStaff = user?.role === 'staff';

   // Staff reach the Shop when they hold any of its grants — packing, payments
   // or products. The panel itself then shows only the tabs they can use.
   const shopGrants = ['shop-orders', 'shop-payments', 'shop-products'];
   const canSeeShop =
      isAdmin || (user?.staffPermissions ?? []).some((p: string) => shopGrants.includes(p));

   const shopPanelLink = {
      icon: 'store-3-line' as const,
      label: 'Admin Panel (Shop)',
      onClick: () => {
         navigate('/admin-panel-shop');
      },
   };

   // Posts is its own screen with its own grant, so a writer can be given it
   // without the Piso WiFi panel or anything else in Settings.
   const canSeePosts = isAdmin || (user?.staffPermissions ?? []).includes('posts');

   const postsPanelLink = {
      icon: 'file-copy-line' as const,
      label: 'Admin Panel (Posts)',
      onClick: () => {
         navigate('/admin-panel-posts');
      },
   };

   // Staff used to be offered the Piso WiFi panel whatever they held, from when
   // it was the only staff surface. Now that Shop and Posts are their own
   // screens, somebody granted only those was still shown a panel where every
   // tab was hidden — an empty page that reads as broken. Offer it only to
   // someone who holds a tab that is actually in it.
   const canSeeWifi = isAdmin || canOpenWifiPanel(user?.staffPermissions ?? []);

   const wifiPanelLink = {
      icon: 'router-line' as const,
      label: 'Admin Panel (Piso WiFi)',
      onClick: () => {
         navigate('/admin-panel-wifi');
      },
   };

   // ISP Business sells to a different person than piso wifi does — an
   // operator with their own PPPoE MikroTik, who may own no vendo at all — so
   // its price list is its own panel rather than a section inside the piso
   // one. Admin only, and only in a build where ISP is switched on.
   const ispPanelLink = {
      icon: 'global-line' as const,
      label: 'Admin Panel (ISP)',
      onClick: () => {
         navigate('/admin-panel-isp');
      },
   };

   const dropdownData: DropdownActionType<null>[] = [
      profileMenu.action,
      // Team access (Ray 2026-10-05): switch to an account shared with me.
      ...(isAdmin
         ? [
              {
                 icon: 'shield-user-line' as const,
                 label: 'Admin Panel (Voucher)',
                 onClick: () => {
                    navigate('/admin-panel-voucher');
                 },
              },
              wifiPanelLink,
              ...(ISP_ENABLED ? [ispPanelLink] : []),
              {
                 icon: 'star-line' as const,
                 label: 'Admin Panel (Points)',
                 onClick: () => {
                    navigate('/admin-panel-points');
                 },
              },
              postsPanelLink,
              shopPanelLink,
           ]
         : isStaff
           ? [
                ...(canSeeWifi ? [wifiPanelLink] : []),
                ...(canSeeShop ? [shopPanelLink] : []),
                ...(canSeePosts ? [postsPanelLink] : []),
             ]
           : []),
      // Support is shown to non-admins (staff + operators).
      ...(!isAdmin
         ? [
              {
                 icon: 'customer-service-2-line' as const,
                 label: 'Support',
                 onClick: () => {
                    // Was a jump straight to one personal Facebook page. Now it
                    // shows who is actually answering and what each of them
                    // handles, so a billing question does not land with the
                    // person who fixes NodeMCUes.
                    handleShow({
                       template: 'support-team',
                       title: 'Talk to our team',
                       windowType: 'desktop',
                       data: null,
                    });
                 },
              },
           ]
         : []),
      {
         icon: 'logout-box-r-line' as const,
         label: 'Logout',
         onClick: () => {
            clearAuthWithCache();
            navigate('/', { replace: true });
         },
      },
   ];


   useEffect(() => {
      if (show) {
         document.body.style.overflow = 'hidden';
      } else {
         document.body.style.overflow = 'auto';
      }
      return () => {
         document.body.style.overflow = 'auto';
      };
   }, [show]);

   // const xxxx = JSON.parse(localStorage.getItem('x-user') || '{}');

   // console.log(`xxxx--`, xxxx);

   // An unverified account can't use any dashboard data — the server rejects
   // every devices/sessions call with "Please verify your email", and those
   // pages poll every 10s, so leaving them mounted behind the blocker spams a
   // red toast every few seconds (see the stacked errors on the verify
   // screen). Render ONLY the blocker until the email is confirmed: no
   // dashboard page mounts, so nothing polls and nothing can error.
   if (user && user.verified === false) {
      return (
         <div>
            <AppToasts />
            <VerifyEmailBlocker />
         </div>
      );
   }

   return (
      <div>
         <AppToasts />
         <Wrapper onClick={handleClickOutside}>
            <PromptWrapper
               show={show}
               template={template ?? ''}
               title={title ?? ''}
               data={uiData}
               actions={actions}
            />
            <SaveChangesModal show={showSave} />
            <Content>
               <SideNav active={active} />
               <Main>
                  <TopHeader>
                     <TopHeaderLeft>
                        <TopHeaderTitle>Dashboard</TopHeaderTitle>
                        {IS_STAGING && (
                           <StagingBadge title="You are on the STAGING environment — NOT production. Changes here do not affect live customers.">
                              🧪 <StagingWord>STAGING</StagingWord>
                           </StagingBadge>
                        )}
                        {!acting && <SurfaceSwitcher />}
                     </TopHeaderLeft>
                     <TopRight>
                        <AccountStack />
                        {!acting && (
                           <ShareBtn type="button" onClick={() => setShareOpen(true)} title="Share your account with someone who helps you">
                              {/* "Add person": the + sits clear of the head (Ray 2026-10-18). */}
                              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                 <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                 <circle cx="9" cy="7" r="4" />
                                 <path d="M19 8v6M22 11h-6" />
                              </svg>
                              <span>Share</span>
                           </ShareBtn>
                        )}
                        {isDistributor && !acting && (
                           <DistributorBadge title="You are an approved SmartFi Distributor">
                              <DistributorBadgeIcon>🤝</DistributorBadgeIcon>
                              <BadgeLabel>DISTRIBUTOR</BadgeLabel>
                           </DistributorBadge>
                        )}
                        {isReseller && !acting && (
                           <ResellerBadge title="You are an approved SmartFi Reseller">
                              <DistributorBadgeIcon>🛍️</DistributorBadgeIcon>
                              <BadgeLabel>RESELLER</BadgeLabel>
                           </ResellerBadge>
                        )}
                        {/* Phones: in the bottom menu instead (SideNav). */}
                        {!phone && <NotificationBell />}
                        <CommunityButton />
                        <ThemeToggle
                           onClick={toggleTheme}
                           aria-label="Toggle theme"
                           title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}
                        >
                           {themeMode === 'light' ? (
                              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                 <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                              </svg>
                           ) : (
                              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                 <circle cx="12" cy="12" r="4" />
                                 <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                              </svg>
                           )}
                        </ThemeToggle>
                     <ProfileWrapper id="profile">
                        <ProfileContent id="profile">
                           <ProfileName id="profile">
                              {myProfile?.name || user?.name || 'Guest'}
                           </ProfileName>
                           <ProfileInitial id="profile">
                              {myProfile?.avatar || user?.avatar ? (
                                 <ProfilePhoto id="profile" src={(myProfile?.avatar || user?.avatar) as string} alt="" />
                              ) : (myProfile?.name || user?.name) ? (
                                 (myProfile?.name || user?.name || 'G').charAt(0).toUpperCase()
                              ) : (
                                 'G'
                              )}
                           </ProfileInitial>
                        </ProfileContent>

                        {outsideClickId === 'profile' && (
                           <Dropdown
                              active={outsideClickId === `profile`}
                              actions={dropdownData}
                              width="150px"
                              textdecoration="normal"
                              top="40px"
                              right="0"
                              // selected={selected}
                              // selected={isValidNormalTextValue(value) ? value : ''}
                           />
                        )}
                     </ProfileWrapper>
                     </TopRight>
                  </TopHeader>
                  {shareOpen && <ShareDialog onClose={() => setShareOpen(false)} />}
                  {profileMenu.modal}
                  <div className="container">
                     <BannerCol>
                        <HelpingBanner />
                        <PendingInviteBanner />
                     </BannerCol>
                     {children}
                  </div>
               </Main>
            </Content>
         </Wrapper>
      </div>
   );
};

const Wrapper = styled.div``;
const Content = styled.div`
   display: grid;
   /* Same as the sidebar's own width (250px): a wider column left an empty strip beside the top bar (Ray 2026-10-27). */
   grid-template-columns: 250px minmax(0, 1fr);
   /* min-height, not height: the page grows, so the sidebar can stay stuck beside it (Ray 2026-10-27). */
   min-height: 100vh;
   @media (max-width: 768px) {
     grid-template-columns: 1fr;
   }
`;
const Main = styled.div`
   /* background-color: ${colors.GRAY14}; */
   position: relative;
   min-width: 0;
   @media (max-width: 768px) {
      padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
      /* clip, not hidden: hidden makes this a scroll box and the sticky header would stop sticking. */
      overflow-x: clip;
      max-width: 100vw;
   }
`;

// Stays at the top while the page scrolls (Ray 2026-10-27): bell, chat and profile always in reach.
const TopHeader = styled.div`
   position: sticky;
   top: 0;
   z-index: 40;
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 12px;
   padding: 5px 1rem;
   background-color: var(--app-surface);
   border-bottom: 1px solid ${colors.GRAY14};
   @media (max-width: 768px) {
      padding: 6px 12px;
      gap: 10px;
   }
`;
const TopHeaderTitle = styled.p`
   font-size: 16px;
   font-weight: 700;
   /* The sidebar/bottom bar already names the page; the phone needs the room. */
   @media (max-width: 768px) { display: none; }
`;
const TopHeaderLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
   min-width: 0;
   @media (max-width: 768px) {
      gap: 8px;
      flex: 1;
      overflow: hidden;
      white-space: nowrap;
      * { white-space: nowrap; }
   }
`;
const ProfileWrapper = styled.div`
   position: relative;
   cursor: pointer;
`;
const ProfileContent = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 5px;
   border-radius: 5px;
   transition: background 0.15s;

   &:hover {
      background-color: ${colors.GRAY13};
   }
`;
const ProfileName = styled.p`
   font-size: 15px;
   font-weight: 600;
   @media (max-width: 768px) { display: none; }
`;
const TopRight = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-shrink: 0;
   @media (max-width: 768px) { gap: 8px; }
`;

// On mobile the role pill collapses to just its icon so it can never push the
// profile button off-screen (it used to overflow → profile unclickable).
const BadgeLabel = styled.span`
   @media (max-width: 768px) { display: none; }
`;

const DistributorBadge = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   border-radius: 20px;
   background: linear-gradient(135deg, #f59e0b, #fbbf24);
   color: #422006;
   font-size: 10px;
   font-weight: 900;
   letter-spacing: 0.08em;
   box-shadow: 0 2px 8px rgba(245, 158, 11, 0.35);
   cursor: default;
   user-select: none;
`;

// True when the dashboard is pointed at the staging API (api-staging.*). Used to
// render an unmistakable STAGING badge so the operator never confuses staging
// with production when switching between the two. Never shows on prod.
const IS_STAGING = /staging/i.test(import.meta.env.VITE_API_URL || '');

// On a phone the flask alone says it: the word took the product switcher's room.
const StagingWord = styled.span`
   @media (max-width: 768px) { display: none; }
`;
const StagingBadge = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 12px;
   border-radius: 20px;
   background: linear-gradient(135deg, #dc2626, #ef4444);
   color: #fff;
   font-size: 11px;
   font-weight: 900;
   letter-spacing: 0.12em;
   box-shadow: 0 2px 10px rgba(220, 38, 38, 0.45);
   cursor: default;
   user-select: none;

   @media (max-width: 768px) {
      padding: 3px 8px;
      font-size: 10px;
      letter-spacing: 0.06em;
      white-space: nowrap;
   }`;

const ResellerBadge = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   border-radius: 20px;
   background: linear-gradient(135deg, #7c3aed, #a78bfa);
   color: #f5f3ff;
   font-size: 10px;
   font-weight: 900;
   letter-spacing: 0.08em;
   box-shadow: 0 2px 8px rgba(124, 58, 237, 0.35);
   cursor: default;
   user-select: none;
`;

const DistributorBadgeIcon = styled.span`
   font-size: 13px;
`;

/* Same column as the page content (Vendo.tsx Wrapper: 1200px centered). */
const BannerCol = styled.div`
   max-width: 1200px;
   margin: 0 auto;
   padding: 0 1.5rem;
   @media (max-width: 768px) {
      padding: 0 0.75rem;
   }
`;
const ShareBtn = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   height: 36px;
   padding: 0 12px;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY13};
   background: ${colors.GRAY14};
   color: ${colors.GRAY1};
   font-weight: 600;
   font-size: 13px;
   cursor: pointer;
   &:hover { border-color: ${colors.GRAY3}; }
   @media (max-width: 768px) { span { display: none; } padding: 0 9px; }
`;
const ThemeToggle = styled.button`
   width: 36px;
   height: 36px;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY13};
   background: ${colors.GRAY14};
   color: ${colors.GRAY1};
   cursor: pointer;
   display: grid;
   place-items: center;
   transition: background 0.15s, color 0.15s, border-color 0.15s;
   &:hover {
      background: ${colors.BLUE_SUPER_LIGHT};
      color: ${colors.PRIMARY};
      border-color: ${colors.PRIMARY};
   }
`;

const ProfilePhoto = styled.img`
   width: 100%;
   height: 100%;
   border-radius: 50%;
   object-fit: cover;
`;

const ProfileInitial = styled.div`
   overflow: hidden;
   width: 30px;
   height: 30px;
   background-color: ${colors.PRIMARY};
   color: white;
   border-radius: 50%;
   display: flex;
   justify-content: center;
   align-items: center;
   font-size: 15px;
   font-weight: 600;
`;

export default DashboardLayout;
